import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore } from './store/authStore'
import Layout from './components/Layout'
import PortalLayout from './components/PortalLayout'
import Login from './pages/Login'
import AppSelect from './pages/AppSelect'
import SetupAccount from './pages/SetupAccount'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import ViewerDashboard from './pages/ViewerDashboard'
import ServerDetail from './pages/ServerDetail'
import Licenses from './pages/Licenses'
import Harbor from './pages/Harbor'
import PortalUsers from './pages/portal/Users'
import PortalCompanies from './pages/portal/Companies'
import PortalCompanyDetail from './pages/portal/CompanyDetail'
import Surveys from './pages/Surveys'
import SurveyDetail from './pages/SurveyDetail'
import DeviceSpecs from './pages/DeviceSpecs'
import DeviceSpecDetail from './pages/DeviceSpecDetail'
import { APP_EDGEMANAGER, SCOPE_EDGE_COMPANIES, SCOPE_EDGE_USERS } from './config/apps'
import { startSessionKeepalive } from './auth/session'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
})

// 브라우저 탭 제목: 로그인·앱 선택·계정 설정·조직 관리(콘솔)처럼 특정 앱 밖에서는 공용 브랜드 "EDGE",
// EdgeManager 화면에 들어오면 "EdgeManager". (보드 등 다른 앱은 자기 웹에서 자기 이름을 붙인다.)
const BRAND_ROUTES = ['/login', '/apps', '/setup-account', '/forgot-password', '/reset-password', '/admin']

function DocumentTitle() {
  const { pathname } = useLocation()
  useEffect(() => {
    document.title = BRAND_ROUTES.some(p => pathname.startsWith(p)) ? 'EDGE' : 'EdgeManager'
  }, [pathname])
  return null
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  return isAuthenticated() ? <>{children}</> : <Navigate to="/login" replace />
}

/**
 * EDGE 콘솔(조직 관리) — 사용자·회사 관리는 특정 앱이 아니라 플랫폼 기능이라 AuthedApp(EdgeManager) 밖에 둔다.
 * 보드만 구독한 회사(aud 에 EdgeManager 없음)의 Admin/Manager 도 여기서 사용자를 초대·관리할 수 있다
 * (서버도 같은 구분 — Users/Companies API 는 Platform 인가 정책).
 */
function PortalAdmin() {
  const { role, can } = useAuthStore()
  // 사용자 관리: Admin·Manager(자기 회사) 또는 edge:users(전 회사) / 회사 관리: Admin 또는 edge:companies
  const canUsers = role === 'Manager' || can(SCOPE_EDGE_USERS)
  const canCompanies = can(SCOPE_EDGE_COMPANIES)
  if (!canUsers && !canCompanies) return <Navigate to="/apps" replace />

  return (
    <PortalLayout>
      <Routes>
        {canUsers && <Route path="users" element={<PortalUsers />} />}
        {canCompanies && <Route path="companies" element={<PortalCompanies />} />}
        {canCompanies && <Route path="companies/:id" element={<PortalCompanyDetail />} />}
        <Route path="*" element={<Navigate to={canUsers ? '/admin/users' : '/admin/companies'} replace />} />
      </Routes>
    </PortalLayout>
  )
}

/** 조직 관리가 EdgeManager 내부 라우트였던 시절의 경로(북마크·메일 링크) → 콘솔로 안내 */
function LegacyCompanyRedirect() {
  const { id = '' } = useParams<{ id: string }>()
  return <Navigate to={`/admin/companies/${id}`} replace />
}

function AuthedApp() {
  const { role, apps } = useAuthStore()

  // 회사에서 EdgeManager 를 꺼둔 계정은 앱 선택 화면으로. apps 가 비어 있으면 이 배포 이전에 로그인한
  // 세션(aud 미저장)이므로 통과시킨다 — 첫 API 401 → 리프레시에서 apps 가 채워진다.
  if (apps.length > 0 && !apps.includes(APP_EDGEMANAGER)) return <Navigate to="/apps" replace />

  // 뷰어 계정은 다른 페이지 없이 전용 모니터 화면만 본다. 서버 상세(실시간/조회)는
  // 그 화면 안에서 모달로 띄워서 보여준다 — 라우팅으로 못 벗어나게 격리 유지.
  if (role === 'Viewer') return <ViewerDashboard />

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/servers/:id" element={<ServerDetail />} />
        {/* Edge 서버 관리는 대시보드(연동 대기 카드) + 서버 상세로 통합 */}
        <Route path="/servers" element={<Navigate to="/" replace />} />
        <Route path="/licenses" element={<Licenses />} />
        <Route path="/harbor" element={<Harbor />} />
        <Route path="/surveys/:id" element={<SurveyDetail />} />
        <Route path="/surveys" element={<Surveys />} />
        <Route path="/device-specs/:id" element={<DeviceSpecDetail />} />
        <Route path="/device-specs" element={<DeviceSpecs />} />
      </Routes>
    </Layout>
  )
}

export default function App() {
  // 액세스 토큰 만료 전 선제 갱신 (auth/session.ts)
  useEffect(() => startSessionKeepalive(), [])

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <DocumentTitle />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/setup-account" element={<SetupAccount />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/apps" element={<PrivateRoute><AppSelect /></PrivateRoute>} />
          <Route path="/admin/*" element={<PrivateRoute><PortalAdmin /></PrivateRoute>} />
          {/* 구 경로 호환 — 조직 관리가 EdgeManager 안에 있던 시절의 북마크 */}
          <Route path="/user-management" element={<Navigate to="/admin/users" replace />} />
          <Route path="/companies" element={<Navigate to="/admin/companies" replace />} />
          <Route path="/companies/:id" element={<LegacyCompanyRedirect />} />
          <Route path="/*" element={
            <PrivateRoute>
              <AuthedApp />
            </PrivateRoute>
          } />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
